import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Play, X } from "lucide-react";

const CHART_SAMPLE = [{ name: "Mon", value: 8 }, { name: "Tue", value: 14 }, { name: "Wed", value: 6 }, { name: "Thu", value: 11 }];
const PIE_COLORS = ["#22D3EE", "#F59E0B", "#34D399", "#818CF8"];
const MARKERS = { dot: "•", square: "▪", arrow: "➤", check: "✓", dash: "–" };

export const marker = (style, i) => (style === "number" ? `${i + 1}.` : MARKERS[style] || "•");

export function SlideTable({ table, onChange, fg, readOnly = false }) {
  const cells = table.cells || {};
  const set = (r, c, v) => onChange({ ...table, cells: { ...cells, [`${r}-${c}`]: v } });
  return (
    <table data-testid="slide-table" className="w-full text-xs border-collapse" style={{ color: fg }}>
      <tbody>
        {Array.from({ length: table.rows }).map((_, r) => (
          <tr key={r}>
            {Array.from({ length: table.cols }).map((__, c) => (
              <td key={c} className="border p-0" style={{ borderColor: `${fg}66` }}>
                <input data-testid={`slide-table-cell-${r}-${c}`} value={cells[`${r}-${c}`] || ""} onChange={(e) => set(r, c, e.target.value)} placeholder={r === 0 && !readOnly ? "Header" : ""} readOnly={readOnly}
                  className={`w-full bg-transparent outline-none px-1.5 py-1 placeholder:opacity-40 ${r === 0 ? "font-bold" : ""}`} style={{ color: fg }} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function SlideVideo({ video, autoPlay = false }) {
  if (!video.src) return (
    <div data-testid="slide-video" className="relative rounded-lg overflow-hidden bg-black/80 aspect-video flex flex-col items-center justify-center text-white">
      <div className="w-12 h-12 rounded-full bg-white/90 text-black flex items-center justify-center"><Play className="w-6 h-6 ml-0.5" /></div>
      <p className="text-xs mt-2 px-2 text-center">{video.label}</p>
    </div>
  );
  return (
    <div data-testid="slide-video" className="relative rounded-lg overflow-hidden bg-black aspect-video">
      <video data-testid="slide-video-player" src={video.src} poster={video.poster} controls playsInline preload="none" autoPlay={autoPlay} muted={autoPlay} className="w-full h-full object-contain" />
      <span className="absolute top-1.5 left-2 text-[10px] text-white/90 bg-black/50 px-1.5 rounded pointer-events-none">{video.label} · {video.duration}</span>
      {video.credit && <span className="absolute bottom-1.5 right-2 text-[9px] text-white/70 bg-black/40 px-1 rounded pointer-events-none" data-testid="slide-video-credit">{video.credit}</span>}
    </div>
  );
}

export function SlideMedia({ slide, theme, fg, updateSlide, readOnly = false, autoPlayVideo = false }) {
  const rm = (key, testid, label) => (
    <button data-testid={testid} onClick={() => updateSlide({ [key]: null })} className="text-[11px] opacity-70 hover:opacity-100 underline inline-flex items-center gap-0.5"><X className="w-3 h-3" /> {label}</button>
  );
  return (
    <div className="space-y-2 min-w-0">
      {slide.image && <div className="relative rounded-lg overflow-hidden"><img src={slide.image.url} alt={slide.image.label} className="w-full h-full object-cover max-h-40" crossOrigin="anonymous" /></div>}
      {slide.chart && (
        <div className="bg-white/95 rounded-lg p-2" style={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            {slide.chart.type === "bar" ? (
              <BarChart data={CHART_SAMPLE}><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="value" fill={theme.accent} radius={[4, 4, 0, 0]} /></BarChart>
            ) : (
              <PieChart><Pie data={CHART_SAMPLE} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={55}>{CHART_SAMPLE.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie><Legend wrapperStyle={{ fontSize: 10 }} /></PieChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
      {slide.table && <SlideTable table={slide.table} onChange={(t) => updateSlide({ table: t })} fg={fg} readOnly={readOnly} />}
      {slide.video && <SlideVideo video={slide.video} autoPlay={autoPlayVideo} />}
      {!readOnly && (
        <div className="flex gap-3 flex-wrap">
          {slide.image && rm("image", "slide-image-remove", "Remove image")}
          {slide.chart && rm("chart", "slide-chart-remove", "Remove chart")}
          {slide.table && rm("table", "slide-table-remove", "Remove table")}
          {slide.video && rm("video", "slide-video-remove", "Remove video")}
        </div>
      )}
    </div>
  );
}
